<template>
  <b-card no-body>
    <div class="ml-2 mr-2">
      <b-row>
        <b-col
          cols="12"
          md="12"
        >
          <h5 class="section-label mb-2 mt-2">
            {{ $t('Gateway Settlement Account Setup') }}
          </h5>

          <b-row class="mb-2">
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <TableEntries
                v-model="perPage"
                :options="perPageOptions"
              />
              <Button
                v-if="getUserPermission('CREATE_TRANSFER_GATEWAY_SETTLEMENT_ACCT')"
                :label="$t('Add')"
                size="md"
                @click="openAddModal"
              />
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-lg-end">
                <Button
                  :label="$t('Filter')"
                  size="md"
                  icon="FilterIcon"
                  show-icon
                  class="mr-1"
                  @click="showModal('modal-gateway-settlement-filter')"
                />
                <Button
                  :label="$t('Reset Filter')"
                  size="md"
                  icon="RotateCwIcon"
                  show-icon
                  @click="resetFilter"
                />
              </div>
            </b-col>
          </b-row>
          <b-table
            ref="gatewaySettlementTable"
            :busy="isBusy"
            :per-page="pageSize"
            :current-page="currentPage"
            :items="gatewaySettlementAccountList"
            responsive
            :fields="translatedTableHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"

            class="position-relative"
          >
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner
                  class="align-middle"
                  variant="primary"
                />
              </div>
            </template>
            <template #cell(SN)="data">
              {{ getOverallIndex(data.index) }}
            </template>

            <template #cell(isActive)="data">
              <b-badge
                pill
                :variant="getStatusColor(data.item.isActive ? 'Active' : 'Inactive')"
              >
                {{ $t(data.item.isActive ? 'Active' : 'Inactive') }}
              </b-badge>
            </template>

            <template #cell(actions)="data">
              <div class="text-nowrap">
                <b-dropdown
                  variant="link"
                  toggle-class="p-0"
                  no-caret
                  :right="$store.state.appConfig.isRTL"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-item
                    v-if="getUserPermission('VIEW_TRANSFER_GATEWAY_SETTLEMENT_ACCT')"
                    @click="onSelectGatewaySettlementItem(data.item, $event.target, 'view')"
                  >
                    <feather-icon icon="EyeIcon" />
                    <span class="align-middle ml-50"> {{ $t('View') }} </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="getUserPermission('UPDATE_TRANSFER_GATEWAY_SETTLEMENT_ACCT')"
                    @click="onSelectGatewaySettlementItem(data.item, $event.target, 'edit')"
                  >
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="getUserPermission('APPLY_GATEWAY_SETTLEMENT_ACCOUNT')"
                    @click="settlementGatewayAccountsStatus(data.item, $event.target)"
                  >
                    <feather-icon icon="CheckSquareIcon" />
                    <span class="align-middle ml-50">{{ $t('Apply Gateway Settlement Account') }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>

          <TablePagination
            v-model="currentPage"
            :from="from"
            :to="to"
            :total-rows="totalEntries"
            :per-page="perPage"
          />

          <!-- Add / Edit / View -->
          <b-modal
            id="modal-gateway-settlement-add"
            ref="modal-gateway-settlement-add"
            no-close-on-backdrop
            :ok-title="buttonLabel"
            :cancel-title="$t('Cancel')"
            cancel-variant="outline-secondary"
            :ok-disabled="isLoading"
            centered
            size="lg"
            :title="modalTitle"
            @ok="handleSubmit"
            @hidden="resetModal"
          >
            <validation-observer ref="gatewaySettlementRules">
              <b-form @submit.prevent="handleSubmit">
                <b-row>
                  <b-col
                    cols="12"
                    class="mb-0 mt-2"
                  />
                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <SelectField
                      id="gsa-gateway"
                      v-model="gatewaySettlementAccountformData.gateway"
                      :label="$t('Gateway')"
                      :options="gatewayProviderOptions"
                      :error-name="$t('Gateway')"
                      rules="required"
                      item="label"
                      :reduce="option => option"
                      :placeholder="$t('Select')"
                      :readonly="isReadonly"
                    />
                  </b-col>

                  <b-col md="6">
                    <InputField
                      id="gsa-account-number"
                      v-model="gatewaySettlementAccountformData.accountNumber"
                      :label="$t('Account Number')"
                      :placeholder="$t('Enter account number')"
                      :error-name="$t('Account Number')"
                      rules="required"
                      for="gsa-account-number"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="6">
                    <InputField
                      id="gsa-account-name"
                      v-model="gatewaySettlementAccountformData.accountName"
                      :label="$t('Account Name')"
                      :placeholder="$t('Enter account name')"
                      :error-name="$t('Account Name')"
                      rules="required"
                      for="gsa-account-name"
                      :readonly="isReadonly"
                    />
                  </b-col>

                  <b-col md="6">
                    <SelectField
                      id="gsa-transfer-settlement-mode"
                      v-model="gatewaySettlementAccountformData.transferSettlementMode"
                      :label="$t('Transfer Settlement Mode')"
                      :options="gatewaySettlementAccountOptions"
                      :error-name="$t('Transfer Settlement Mode')"
                      rules="required"
                      item="label"
                      :reduce="option => option.value"
                      :placeholder="$t('Select')"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>

            <template
              v-if="isReadonly"
              #modal-footer
            >
              <div />
            </template>
          </b-modal>

          <!-- Filter -->
          <b-modal
            id="modal-gateway-settlement-filter"
            ref="modal-gateway-settlement-filter-ref"
            :ok-title="$t('Apply')"
            :cancel-title="$t('Cancel')"
            cancel-variant="outline-secondary"
            no-close-on-backdrop
            centered
            size="lg"
            :title="$t('Filters')"
            modal-footer
            @ok="fetchSettlemenGatewaytAccounts"
            @cancel="resetFiltersOnly"
          >
            <b-row class="match-height">
              <b-col md="6">
                <InputField
                  id="filter-account-number"
                  v-model="filter.accountNumber"
                  :label="$t('Account Number')"
                  :placeholder="$t('Enter account number')"
                  for="filter-account-number"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="filter-account-name"
                  v-model="filter.accountName"
                  :label="$t('Account Name')"
                  :placeholder="$t('Enter account name')"
                  for="filter-account-name"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="filter-transfer-settlement-mode"
                  v-model="filter.transferSettlementMode"
                  :label="$t('Transfer Settlement Mode')"
                  :placeholder="$t('Enter transfer settlement mode')"
                  for="filter-transfer-settlement-mode"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="filter-gateway"
                  v-model="filter.gateway"
                  :label="$t('Gateway')"
                  :reduce="option => option"
                  :placeholder="$t('Select')"
                  :options="gatewayProviderOptions"
                  item="label"
                />
              </b-col>
            </b-row>
          </b-modal>
        </b-col>
      </b-row>
    </div>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal,
  BForm, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { required } from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '../../../../../helpers/permissions'
import getStatusColor from '../../../../../helpers/getStatusColor'

export default {
  components: {
    BBadge,
    BCard,
    BRow,
    BCol,
    BTable,
    BSpinner,
    BModal,
    BForm,
    ValidationObserver,
    BDropdown,
    BDropdownItem,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      required,
      modalTitle: this.$t('Gateway Settlement Account'),
      isEditMode: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'transferSettlementMode', sortable: true, label: 'Transfer Settlement Mode' },
        { key: 'gateway', sortable: true, label: 'Gateway' },
        { key: 'isActive', sortable: true, label: 'Status' },
        { key: 'actions' },
      ],
      gatewaySettlementAccountformData: {
        accountNumber: '',
        accountName: '',
        transferSettlementMode: '',
        gateway: '',
      },
      gatewaySettlementAccountList: [],
      gatewatSettlementAccountsConfigurations: [],
      gatewaySettlementAccountOptions: [
        { label: 'FlexibleLocalBank', value: 'FlexibleLocalBank' },
        { label: 'FlexibleInterBank', value: 'FlexibleInterBank' },
      ],
      gatewayProviderOptions: [],
      filter: {
        accountNumber: '',
        accountName: '',
        transferSettlementMode: '',
        gateway: '',
      },
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
  },
  watch: {
    currentPage() {
      this.fetchSettlemenGatewaytAccounts()
    },
    perPage() {
      this.fetchSettlemenGatewaytAccounts()
    },
  },
  mounted() {
    this.fetchSettlemenGatewaytAccounts()
    this.fetchGatewayProviders()
  },
  methods: {
    ...mapActions({
      getGatewaySetlementAccounts: 'eChannels/getGatewaySetlementAccounts',
      addGatewaySettlementAccount: 'eChannels/addGatewaySettlementAccount',
      updateGatewaySettlementAccount: 'eChannels/updateGatewaySettlementAccount',
      getGatewayProviders: 'eChannels/getGatewayProviders',
      updateGatewaySettlementAccountStatus: 'eChannels/updateGatewaySettlementAccountStatus',
    }),
    openAddModal() {
      this.resetModal()
      this.modalTitle = this.$t('Add Gateway Settlement Account')
      this.buttonLabel = this.$t('Save')
      this.isEditMode = false
      this.isReadonly = false
      this.showModal('modal-gateway-settlement-add')
    },
    onSelectGatewaySettlementItem(items, activity, val) {
      try {
        this.isDisabled = val !== 'edit'
        this.isCustomDisabled = val === 'view' || val === 'edit'
        this.gatewaySettlementAccountformData = {
          ...items,
          accountNumber: items.accountNumber || '',
          accountName: items.accountName || '',
          transferSettlementMode: items.transferSettlementMode || '',
          gateway: items.gateway || '',
        }
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Gateway Settlement Account') : this.$t('View Gateway Settlement Account')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-gateway-settlement-add')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    handleSubmit(bvModalEvt) {
      if (bvModalEvt && typeof bvModalEvt.preventDefault === 'function') {
        bvModalEvt.preventDefault()
      }
      this.$refs.gatewaySettlementRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisText = this.$t('this Gateway Settlement Account')
          this.confirm(this.settlementGatewayAccounts, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
        }
      })
    },
    async settlementGatewayAccounts() {
      try {
        this.isLoading = true
        const formData = {
          ...this.gatewaySettlementAccountformData,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.addGatewaySettlementAccount({ formData, permission: eChannelsPermission.CREATE_TRANSFER_GATEWAY_SETTLEMENT_ACCT })
          : await this.updateGatewaySettlementAccount({ formData, permission: eChannelsPermission.UPDATE_TRANSFER_GATEWAY_SETTLEMENT_ACCT })

        if (response && response.status === 200) {
          await this.fetchSettlemenGatewaytAccounts()
          this.hideModal('modal-gateway-settlement-add')
          const requestText = this.$t('Your request to')
          const successText = this.$t('Gateway Settlement Account was successful')
          this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          error.response.data.errors.forEach(responseError => {
            const requestText = this.$t('Your request to')
            const reasonText = this.$t('Gateway Settlement Account failed. Reason')
            const errorMessage = `${requestText} ${this.buttonLabel} ${reasonText}: ${responseError.errorMessage}`
            this.alertError({ text: errorMessage })
          })
        } else {
          this.alertError({ text: error.message || error })
        }
      } finally {
        this.isLoading = false
      }
    },
    async fetchSettlemenGatewaytAccounts() {
      try {
        this.isBusy = true
        this.hideModal('modal-gateway-settlement-add')
        const response = await this.getGatewaySetlementAccounts({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_TRANSFER_GATEWAY_SETTLEMENT_ACCT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.gatewaySettlementAccountList = data.data
          console.log(this.gatewaySettlementAccountList)
          this.totalCount = data.recordCount
        } else {
          this.gatewaySettlementAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    async fetchGatewayProviders() {
      try {
        const response = await this.getGatewayProviders({
          permission: eChannelsPermission.CONFIGURE_TRANSFER_GL,
        })
        if (response && response.status === 200 && response.data) {
          this.gatewayProviderOptions = response.data
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    settlementGatewayAccountsStatus(items) {
      const { id, isActive } = items
      const actionType = !!items.isActive
      const action = items.isActive ? 'Deactivate' : 'Activate'
      const permission = eChannelsPermission.APPLY_GATEWAY_SETTLEMENT_ACCOUNT

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            isActive,
          }
          const response = await this.updateGatewaySettlementAccountStatus({
            statusValues, routeName: 'Client', permission, actionType, action,
          })
          if (response.status === 200) {
            this.fetchSettlemenGatewaytAccounts()
            const text1 = this.$t('Your request to')
            const text2 = this.$t('apply Gateway was successful')
            this.alertSuccess({ text: `${text1} ${text2}` })
            this.showSpinner = false
          }
        } catch (error) {
          const text1 = this.$t('Your request to')
          const text = this.$t('Gateway Settlement Accounts status failed. Reason')
          this.alertError({ text: `${text1} ${action} ${text}: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const thisText = this.$t('Apply this Gateway?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${thisText}` })
    },

    resetModal() {
      this.gatewaySettlementAccountformData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.isEditMode = false
      this.modalTitle = this.$t('Gateway Settlement Account')
      this.buttonLabel = this.$t('Save')
    },
    resetFiltersOnly() {
      this.filter = {
        accountNumber: '',
        accountName: '',
        transferSettlementMode: '',
        gateway: '',
      }
    },
    resetFilter() {
      this.resetFiltersOnly()
      this.currentPage = 1
      this.fetchSettlemenGatewaytAccounts()
    },
  },
}
</script>
